import React, { useEffect, useRef, useState } from "react";
import { Check, Link2 } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

// The clipboard API needs a secure context and can be refused; the textarea
// route still works in older in-app browsers.
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }
}

/**
 * Copies the kit page's URL. `variant="icon"` is the bare glyph that sits
 * beside a catalogue row's download icon; `variant="button"` matches the
 * kit page's outlined buttons. Safe inside the row's Link: the click never
 * navigates.
 */
export default function CopyLinkButton({ item, variant = "icon" }) {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyText(`${window.location.origin}/kits/${item.id}`);
    setState(ok ? "copied" : "failed");
    if (ok) trackEvent("kit_link_copied", { preset_id: item.id, where: variant === "icon" ? "row" : "detail" });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), 2000);
  };

  const label =
    state === "copied" ? "Link copied" : state === "failed" ? "Couldn't copy the link" : `Copy link to ${item.title}`;

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="inline-flex items-center gap-2 rounded border border-white/10 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-white/5"
      >
        {state === "copied" ? <Check size={16} /> : <Link2 size={16} />}
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy link"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
        state === "copied" ? "text-[#FF5500] opacity-100" : state === "failed" ? "text-red-400 opacity-100" : "text-white opacity-50 hover:opacity-95 active:opacity-80"
      }`}
      style={{ filter: "drop-shadow(0 0 1.5px rgba(255, 255, 255, 0.3))" }}
    >
      {state === "copied" ? <Check size={19} strokeWidth={1.6} /> : <Link2 size={19} strokeWidth={1.6} />}
    </button>
  );
}
